import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router";
import { ChevronLeft, ChevronRight, PanelLeft, PanelRight, Search } from "lucide-react";
import { useAppHistory } from "./useAppHistory";
import styles from "./AppHeader.module.css";

export interface AppHeaderProps {
  isSecondarySidebarOpen: boolean;
  onToggleSecondarySidebar: () => void;
  isAssistantOpen: boolean;
  onAssistantToggle: () => void;
  /**
   * True while a chat waits for the closed right panel: on a phone, Gauss
   * opened a page and did not open the panel over it. The toggle shows a dot.
   */
  hasAssistantHandoff?: boolean;
  onToggleCommandPalette: () => void;
  /** True while a layer covers the top bar (the phone drawer or sheet). */
  inert?: boolean;
}

const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

/** Shown in native `title` tooltips next to header controls (matches keyboard handler below). */
const shortcuts = {
  back: isMac ? "⌘[ or ⌥←" : "Ctrl+[ or Alt+←",
  forward: isMac ? "⌘] or ⌥→" : "Ctrl+] or Alt+→",
  commandPalette: isMac ? "⌘K" : "Ctrl+K",
  chatHome: isMac ? "⌘⇧O" : "Ctrl+Shift+O",
} as const;

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return target.isContentEditable;
}

export function AppHeader({
  isSecondarySidebarOpen,
  onToggleSecondarySidebar,
  isAssistantOpen,
  onAssistantToggle,
  hasAssistantHandoff = false,
  onToggleCommandPalette,
  inert = false,
}: AppHeaderProps) {
  const navigate = useNavigate();
  const { back, forward, canGoBack, canGoForward } = useAppHistory();

  const handleHome = useCallback(() => {
    navigate("/");
  }, [navigate]);

  // Global keyboard shortcuts: ⌘K (palette), ⌘⇧O (home),
  // ⌘[ / ⌘] and Alt+Arrow for back/forward.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const isMeta = event.metaKey || event.ctrlKey;

      if (isMeta && (event.key === "k" || event.key === "K")) {
        event.preventDefault();
        onToggleCommandPalette();
        return;
      }

      if (isEditableTarget(event.target)) return;

      if (isMeta && event.shiftKey && (event.key === "o" || event.key === "O")) {
        event.preventDefault();
        navigate("/");
        return;
      }

      if (isMeta && event.key === "[") {
        event.preventDefault();
        back();
        return;
      }
      if (isMeta && event.key === "]") {
        event.preventDefault();
        forward();
        return;
      }
      if (event.altKey && !event.metaKey && !event.ctrlKey) {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          back();
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          forward();
        }
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [back, forward, navigate, onToggleCommandPalette]);

  return (
    <header className={styles.header} role="banner" inert={inert}>
      <div className={styles.leftCluster}>
        <button
          type="button"
          className={styles.secondaryToggle}
          onClick={onToggleSecondarySidebar}
          aria-pressed={isSecondarySidebarOpen}
          aria-label={
            isSecondarySidebarOpen
              ? "Hide section navigation"
              : "Show section navigation"
          }
          title={
            isSecondarySidebarOpen
              ? "Hide section navigation"
              : "Show section navigation"
          }
        >
          <PanelLeft className={styles.secondaryToggleIcon} aria-hidden="true" />
        </button>
        <button
          type="button"
          className={styles.brand}
          onClick={handleHome}
          aria-label="Go to chat home"
          title={`Chat home (${shortcuts.chatHome})`}
        >
          <span className={styles.brandText}>Parametric</span>
          <span className={styles.brandProduct}>Bastion</span>
        </button>
      </div>

      <div className={styles.centerCluster}>
        <div className={styles.centerNavAndSearch}>
          <button
            type="button"
            className={styles.navButton}
            onClick={back}
            disabled={!canGoBack}
            aria-label="Go back"
            title={`Back — ${shortcuts.back}`}
          >
            <ChevronLeft className={styles.navButtonIcon} aria-hidden="true" />
          </button>
          <button
            type="button"
            className={styles.navButton}
            onClick={forward}
            disabled={!canGoForward}
            aria-label="Go forward"
            title={`Forward — ${shortcuts.forward}`}
          >
            <ChevronRight className={styles.navButtonIcon} aria-hidden="true" />
          </button>
          <button
            type="button"
            className={styles.search}
            onClick={onToggleCommandPalette}
            aria-label="Open command palette"
            title={`Search & command palette — ${shortcuts.commandPalette}`}
          >
            <Search className={styles.searchIcon} aria-hidden="true" />
            <span className={styles.searchPlaceholder}>Search…</span>
            <kbd className={styles.searchShortcut}>{isMac ? "⌘K" : "Ctrl+K"}</kbd>
          </button>
        </div>
      </div>

      <div className={styles.rightCluster}>
        <button
          type="button"
          className={styles.secondaryToggle}
          onClick={onAssistantToggle}
          aria-pressed={isAssistantOpen}
          aria-label={
            isAssistantOpen
              ? "Hide the right panel"
              : hasAssistantHandoff
                ? "Show the right panel. The chat continues there."
                : "Show the right panel"
          }
          title={isAssistantOpen ? "Hide the right panel" : "Show the right panel"}
          data-testid="app-right-panel-toggle"
        >
          <PanelRight className={styles.secondaryToggleIcon} aria-hidden="true" />
          {hasAssistantHandoff ? (
            <span className={styles.handoffDot} aria-hidden="true" />
          ) : null}
        </button>
      </div>
    </header>
  );
}
